import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Chip, Grid, MenuItem, FormControl, InputLabel, Select, Tooltip, Card,
  CardContent, Alert, CircularProgress, TablePagination, InputAdornment, Divider,
  Checkbox, FormControlLabel, useMediaQuery, useTheme, LinearProgress, Snackbar
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon,
  Visibility as ViewIcon, PictureAsPdf as PdfIcon, Email as EmailIcon,
  HowToReg as ConvertIcon, People as PeopleIcon, EventAvailable as EventIcon,
  CheckCircle as CheckIcon, Assignment as AssignmentIcon
} from '@mui/icons-material';
import { onboardingService } from '../services/api';

// OneHub access roles (permissions) — distinct from the job designation
const ACCESS_ROLES = ['actor', 'director', 'editor', 'cinematographer', 'writer', 'lead', 'hr', 'manager', 'finance', 'owner'];

// Designation = the craft / function (what the person does), e.g. an Actor's
// designation is "Acting". These are the selectable designations.
const DESIGNATIONS = ['Acting', 'Direction', 'Writing', 'Cinematography', 'Editing', 'Production', 'Management', 'Operations', 'Finance', 'Human Resources'];

// Normalize a legacy position title (e.g. "Actor", "Junior Actor") to its craft
// designation so the dropdown never shows both "Actor" and "Acting". Titles that
// have no distinct craft pass through unchanged.
const normalizeDesignation = (value) => {
  const v = String(value || '').trim();
  if (!v) return '';
  const p = v.toLowerCase();
  if (DESIGNATIONS.some((d) => d.toLowerCase() === p)) return v; // already a craft
  if (p.includes('act')) return 'Acting';
  if (p.includes('edit')) return 'Editing';
  if (p.includes('cinematograph') || p.includes('camera')) return 'Cinematography';
  if (p.includes('direct')) return 'Direction';
  if (p.includes('writ') || p.includes('script')) return 'Writing';
  if (p.includes('producer') || p.includes('production')) return 'Production';
  if (p.includes('operations')) return 'Operations';
  if (p.includes('finance') || p.includes('monetization')) return 'Finance';
  if (p.includes('human resource') || p === 'hr') return 'Human Resources';
  if (p.includes('manage')) return 'Management';
  return v; // non-craft title (e.g. Sales & Marketing) — keep as-is
};

// Add n days to a YYYY-MM-DD date (timezone-safe). Blank input uses today.
const addDays = (dateStr, n) => {
  const s = dateStr || new Date().toISOString().split('T')[0];
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return '';
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().split('T')[0];
};

// Map a designation (craft) — or a legacy position title — to an access role.
const roleForDesignation = (designation) => {
  const d = String(designation || '').trim().toLowerCase();
  const map = {
    // craft designations
    acting: 'actor', direction: 'director', writing: 'writer', cinematography: 'cinematographer',
    editing: 'editor', production: 'manager', management: 'manager', operations: 'lead',
    finance: 'finance', 'human resources': 'hr',
    // legacy position titles
    actor: 'actor', director: 'director', 'script writer': 'writer', writer: 'writer',
    cinematographer: 'cinematographer', editor: 'editor', manager: 'manager',
    'operations lead': 'lead', lead: 'lead', hr: 'hr', owner: 'owner',
  };
  if (map[d]) return map[d];
  if (d.includes('act')) return 'actor';
  if (d.includes('edit')) return 'editor';
  if (d.includes('cinematograph') || d.includes('camera')) return 'cinematographer';
  if (d.includes('direct')) return 'director';
  if (d.includes('writ')) return 'writer';
  if (d.includes('finance') || d.includes('monetization')) return 'finance';
  if (d.includes('manage') || d.includes('producer') || d.includes('head') || d.includes('operations') || d.includes('lead')) return 'manager';
  return 'actor'; // real-role fallback (HR can change it in the Convert dialog)
};

const STATUSES = ['Offer Draft', 'Offer Sent', 'Offer Accepted', 'Offer Declined', 'Onboarding', 'Completed'];

const STATUS_COLORS = {
  'Offer Draft': 'default',
  'Offer Sent': 'info',
  'Offer Accepted': 'primary',
  'Offer Declined': 'error',
  'Onboarding': 'warning',
  'Completed': 'success',
};

const EMPLOYMENT_TYPES = ['Full-time', 'Part-time', 'Contract', 'Intern'];

const emptyForm = {
  fullName: '', email: '', contactNumber: '', address: '',
  designation: '', department: '', employmentType: 'Full-time', workLocation: 'Karur',
  reportingManager: '', annualCtc: '', monthlyGross: '',
  offerDate: new Date().toISOString().split('T')[0], joiningDate: '', offerValidTill: addDays(null, 30),
  status: 'Offer Draft', probationMonths: 3, noticePeriodDays: 30, offerTerms: '', remarks: '',
  bankAccountNumber: '', bankIfscCode: '', bankName: '', bankBranch: '', accountHolderName: '',
  salaryBreakup: [], checklist: [],
};

// Every field is mandatory except Reporting Manager (and the auto-set Offer
// Sent At, which isn't an input). Salary Breakup is auto-derived, so optional.
const REQUIRED_FIELDS = [
  ['fullName', 'Full Name'], ['email', 'Email'], ['contactNumber', 'Contact Number'], ['address', 'Address'],
  ['designation', 'Designation'], ['department', 'Department'], ['employmentType', 'Employment Type'], ['workLocation', 'Work Location'],
  ['annualCtc', 'Annual CTC'], ['monthlyGross', 'Monthly Gross'],
  ['offerDate', 'Offer Date'], ['joiningDate', 'Joining Date'], ['offerValidTill', 'Offer Valid Till'],
  ['probationMonths', 'Probation (months)'], ['noticePeriodDays', 'Notice Period (days)'], ['status', 'Status'],
  ['accountHolderName', 'Account Holder Name'], ['bankAccountNumber', 'Account Number'], ['bankIfscCode', 'IFSC Code'],
  ['bankName', 'Bank Name'], ['bankBranch', 'Branch'],
  ['offerTerms', 'Additional Offer Terms'], ['remarks', 'Internal Remarks'],
];

const OnboardingManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [openForm, setOpenForm] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [openConvert, setOpenConvert] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [convertData, setConvertData] = useState({ username: '', password: '', role: 'employee', employeeId: '' });
  const [convertError, setConvertError] = useState('');
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' });

  const showToast = (msg, severity = 'success') => setToast({ open: true, msg, severity });

  const loadData = async () => {
    setLoading(true);
    try {
      const params = { page: page + 1, limit: rowsPerPage };
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      const res = await onboardingService.getAll(params);
      setRows(res.data.data || []);
      setTotalCount(res.data.pagination?.total || 0);
    } catch (e) {
      showToast('Failed to load onboarding records', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await onboardingService.getStats();
      setStats(res.data.data);
    } catch (e) { /* non-fatal */ }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [page, rowsPerPage, filterStatus]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadStats(); }, []);

  const handleSearch = () => { setPage(0); loadData(); };

  const openCreate = () => {
    setSelected(null);
    setForm(emptyForm);
    setFormError('');
    setOpenForm(true);
  };

  const openEdit = (row) => {
    setSelected(row);
    setForm({
      ...emptyForm,
      ...row,
      designation: normalizeDesignation(row.designation),
      annualCtc: row.annualCtc ?? '',
      monthlyGross: row.monthlyGross ?? '',
      offerDate: row.offerDate || '',
      joiningDate: row.joiningDate || '',
      offerValidTill: row.offerValidTill || addDays(row.offerDate, 30),
      salaryBreakup: Array.isArray(row.salaryBreakup) ? row.salaryBreakup : [],
      checklist: Array.isArray(row.checklist) ? row.checklist : [],
    });
    setFormError('');
    setOpenForm(true);
  };

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  // Offer Valid Till auto-fills to 30 days after the Offer Date (date of issue).
  const handleOfferDateChange = (e) => {
    const offerDate = e.target.value;
    setForm((f) => ({ ...f, offerDate, offerValidTill: offerDate ? addDays(offerDate, 30) : f.offerValidTill }));
  };

  // Salary breakup handlers
  const addBreakupRow = () => setForm((f) => ({ ...f, salaryBreakup: [...(f.salaryBreakup || []), { component: '', monthly: '', annual: '' }] }));
  const updateBreakup = (i, key, val) => setForm((f) => {
    const arr = [...f.salaryBreakup];
    arr[i] = { ...arr[i], [key]: val };
    return { ...f, salaryBreakup: arr };
  });
  const removeBreakup = (i) => setForm((f) => ({ ...f, salaryBreakup: f.salaryBreakup.filter((_, idx) => idx !== i) }));

  // Checklist handlers (used on edit)
  const toggleChecklist = (i) => setForm((f) => {
    const arr = [...f.checklist];
    arr[i] = { ...arr[i], done: !arr[i].done };
    return { ...f, checklist: arr };
  });

  const handleSave = async () => {
    const missing = REQUIRED_FIELDS.find(([k]) => {
      const v = form[k];
      return v === undefined || v === null || String(v).trim() === '';
    });
    if (missing) {
      setFormError(`"${missing[1]}" is required.`);
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      if (selected) {
        await onboardingService.update(selected.id, form);
        showToast('Onboarding record updated');
      } else {
        await onboardingService.create(form);
        showToast('Onboarding record created');
      }
      setOpenForm(false);
      loadData();
      loadStats();
    } catch (e) {
      setFormError(e.response?.data?.message || 'Failed to save record');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete onboarding record for ${row.fullName}?`)) return;
    try {
      await onboardingService.delete(row.id);
      showToast('Record deleted');
      loadData();
      loadStats();
    } catch (e) {
      showToast('Failed to delete', 'error');
    }
  };

  const handleStatusChange = async (row, status) => {
    try {
      await onboardingService.updateStatus(row.id, status);
      loadData();
      loadStats();
    } catch (e) {
      showToast('Failed to update status', 'error');
    }
  };

  const handleDownloadOffer = async (row) => {
    try {
      const res = await onboardingService.downloadOfferLetter(row.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `OfferLetter_${(row.fullName || 'candidate').replace(/[^a-z0-9]+/gi, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      showToast('Failed to generate offer letter', 'error');
    }
  };

  const handleSendOffer = async (row) => {
    if (!row.email) { showToast('No candidate email on file. Add an email first.', 'warning'); return; }
    if (!window.confirm(`Email the offer letter to ${row.email}?`)) return;
    try {
      await onboardingService.sendOffer(row.id);
      showToast(`Offer letter sent to ${row.email}`);
      loadData();
      loadStats();
    } catch (e) {
      showToast(e.response?.data?.message || 'Failed to send offer letter', 'error');
    }
  };

  const openConvertDialog = async (row) => {
    setSelected(row);
    setConvertError('');
    // Name-based username (portal applicants often share one email).
    const nameSlug = String(row.fullName || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
    setConvertData({
      username: nameSlug || (row.email ? row.email.split('@')[0] : ''),
      password: '',
      designation: normalizeDesignation(row.designation),
      role: roleForDesignation(row.designation),
      employeeId: '',
    });
    setOpenConvert(true);
    // Prefill the next employee ID (editable). Non-blocking.
    try {
      const res = await onboardingService.getNextEmployeeId();
      setConvertData((d) => ({ ...d, employeeId: res.data.employeeId || '' }));
    } catch (e) { /* leave blank; backend still auto-generates on submit */ }
  };

  const handleConvert = async () => {
    // All fields are mandatory except the Temporary Password (blank = default).
    const required = [
      ['username', 'Username'],
      ['employeeId', 'Employee ID'],
      ['designation', 'Designation'],
      ['role', 'Access role'],
    ];
    const missing = required.find(([k]) => !String(convertData[k] || '').trim());
    if (missing) {
      setConvertError(`Please enter the ${missing[1]} before creating the employee.`);
      return;
    }
    setConvertError('');
    try {
      const res = await onboardingService.convertToEmployee(selected.id, convertData);
      showToast(res.data.message || 'Employee created');
      setOpenConvert(false);
      loadData();
      loadStats();
    } catch (e) {
      showToast(e.response?.data?.message || 'Failed to convert to employee', 'error');
    }
  };

  const checklistProgress = (row) => {
    const list = Array.isArray(row.checklist) ? row.checklist : [];
    if (!list.length) return 0;
    return Math.round((list.filter((c) => c.done).length / list.length) * 100);
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
  const fmtMoney = (n) => (n != null && n !== '' ? '₹ ' + Number(n).toLocaleString('en-IN') : '—');

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Employee Onboarding</Typography>
          <Typography variant="body2" color="text.secondary">Manage new hires, generate offer letters, and onboard employees.</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New Onboarding</Button>
      </Box>

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { label: 'Total', value: stats.total, icon: <PeopleIcon />, color: '#1565C0' },
            { label: 'In Progress', value: stats.inProgress, icon: <AssignmentIcon />, color: '#ED6C02' },
            { label: 'Upcoming Joiners (30d)', value: stats.upcomingJoiners, icon: <EventIcon />, color: '#0288D1' },
            { label: 'Completed', value: stats.completed, icon: <CheckIcon />, color: '#2E7D32' },
          ].map((s) => (
            <Grid item xs={6} md={3} key={s.label}>
              <Card>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
                  <Box sx={{ color: s.color }}>{s.icon}</Box>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>{s.value ?? 0}</Typography>
                    <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small" placeholder="Search name, email, designation…" value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 260 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}>
            <MenuItem value="">All Statuses</MenuItem>
            {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={handleSearch}>Search</Button>
      </Paper>

      {/* Table */}
      <Paper>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'action.hover' } }}>
                <TableCell>Candidate</TableCell>
                {!isMobile && <TableCell>Designation</TableCell>}
                {!isMobile && <TableCell>Joining</TableCell>}
                <TableCell>Status</TableCell>
                {!isMobile && <TableCell>Checklist</TableCell>}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && !loading && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No onboarding records yet.</TableCell></TableRow>
              )}
              {rows.map((row) => (
                <TableRow key={row.id} hover onClick={() => { setSelected(row); setOpenView(true); }} sx={{ cursor: 'pointer' }}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{row.fullName}</Typography>
                    <Typography variant="caption" color="text.secondary">{row.email || row.contactNumber || '—'}</Typography>
                  </TableCell>
                  {!isMobile && <TableCell>
                    <Typography variant="body2">{row.designation}</Typography>
                    <Typography variant="caption" color="text.secondary">{row.department || '—'}</Typography>
                  </TableCell>}
                  {!isMobile && <TableCell>{fmtDate(row.joiningDate)}</TableCell>}
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <FormControl size="small" variant="standard">
                      <Select
                        value={row.status}
                        onChange={(e) => handleStatusChange(row, e.target.value)}
                        disableUnderline
                        renderValue={(v) => <Chip size="small" label={v} color={STATUS_COLORS[v] || 'default'} />}
                      >
                        {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </TableCell>
                  {!isMobile && <TableCell sx={{ width: 120 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress variant="determinate" value={checklistProgress(row)} sx={{ flex: 1, height: 6, borderRadius: 3 }} />
                      <Typography variant="caption">{checklistProgress(row)}%</Typography>
                    </Box>
                  </TableCell>}
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="View"><IconButton size="small" onClick={() => { setSelected(row); setOpenView(true); }}><ViewIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(row)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Download Offer Letter"><IconButton size="small" color="primary" onClick={() => handleDownloadOffer(row)}><PdfIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title={row.status === 'Offer Declined' ? 'Offer declined — cannot email' : 'Email Offer Letter'}><span><IconButton size="small" color="info" disabled={!row.email || row.status === 'Offer Declined'} onClick={() => handleSendOffer(row)}><EmailIcon fontSize="small" /></IconButton></span></Tooltip>
                    <Tooltip title={row.createdUserId ? 'Already an employee' : (row.status === 'Offer Declined' ? 'Offer declined — cannot convert' : 'Convert to Employee')}><span><IconButton size="small" color="success" disabled={!!row.createdUserId || row.status === 'Offer Declined'} onClick={() => openConvertDialog(row)}><ConvertIcon fontSize="small" /></IconButton></span></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(row)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div" count={totalCount} page={page} onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      {/* Create / Edit dialog */}
      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>{selected ? 'Edit Onboarding' : 'New Onboarding'}</DialogTitle>
        <DialogContent dividers>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Candidate Details</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}><TextField label="Full Name *" fullWidth size="small" value={form.fullName} onChange={setField('fullName')} /></Grid>
            <Grid item xs={12} md={6}><TextField required label="Email" fullWidth size="small" value={form.email} onChange={setField('email')} /></Grid>
            <Grid item xs={12} md={6}><TextField required label="Contact Number" fullWidth size="small" value={form.contactNumber} onChange={setField('contactNumber')} /></Grid>
            <Grid item xs={12} md={6}><TextField required label="Address" fullWidth size="small" value={form.address} onChange={setField('address')} /></Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Offer Details</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Designation *</InputLabel>
                <Select label="Designation *" value={form.designation || ''} onChange={setField('designation')}>
                  {[...new Set([form.designation, ...DESIGNATIONS].filter(Boolean))].map((label) => (
                    <MenuItem key={label} value={label}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}><TextField required label="Department" fullWidth size="small" value={form.department} onChange={setField('department')} /></Grid>
            <Grid item xs={12} md={4}>
              <FormControl required fullWidth size="small">
                <InputLabel>Employment Type</InputLabel>
                <Select label="Employment Type" value={form.employmentType} onChange={setField('employmentType')}>
                  {EMPLOYMENT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}><TextField required label="Work Location" fullWidth size="small" value={form.workLocation} onChange={setField('workLocation')} /></Grid>
            <Grid item xs={12} md={4}><TextField label="Reporting Manager" fullWidth size="small" value={form.reportingManager} onChange={setField('reportingManager')} /></Grid>
            <Grid item xs={12} md={6}><TextField required label="Annual CTC (₹)" type="number" fullWidth size="small" value={form.annualCtc} onChange={setField('annualCtc')} /></Grid>
            <Grid item xs={12} md={6}><TextField required label="Monthly Gross (₹)" type="number" fullWidth size="small" value={form.monthlyGross} onChange={setField('monthlyGross')} /></Grid>
            <Grid item xs={12} md={4}><TextField required label="Offer Date" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.offerDate || ''} onChange={handleOfferDateChange} /></Grid>
            <Grid item xs={12} md={4}><TextField required label="Joining Date" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.joiningDate || ''} onChange={setField('joiningDate')} /></Grid>
            <Grid item xs={12} md={4}><TextField required label="Offer Valid Till" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.offerValidTill || ''} onChange={setField('offerValidTill')} helperText="Auto: 30 days from Offer Date" /></Grid>
            <Grid item xs={6} md={4}><TextField required label="Probation (months)" type="number" fullWidth size="small" value={form.probationMonths} onChange={setField('probationMonths')} /></Grid>
            <Grid item xs={6} md={4}><TextField required label="Notice Period (days)" type="number" fullWidth size="small" value={form.noticePeriodDays} onChange={setField('noticePeriodDays')} /></Grid>
            <Grid item xs={12} md={4}>
              <FormControl required fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={form.status} onChange={setField('status')}>
                  {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" color="primary">Salary Breakup (optional)</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addBreakupRow}>Add Row</Button>
          </Box>
          {(form.salaryBreakup || []).map((c, i) => (
            <Grid container spacing={1} key={i} sx={{ mb: 1 }}>
              <Grid item xs={5}><TextField label="Component" size="small" fullWidth value={c.component} onChange={(e) => updateBreakup(i, 'component', e.target.value)} /></Grid>
              <Grid item xs={3}><TextField label="Monthly" size="small" type="number" fullWidth value={c.monthly} onChange={(e) => updateBreakup(i, 'monthly', e.target.value)} /></Grid>
              <Grid item xs={3}><TextField label="Annual" size="small" type="number" fullWidth value={c.annual} onChange={(e) => updateBreakup(i, 'annual', e.target.value)} /></Grid>
              <Grid item xs={1}><IconButton color="error" onClick={() => removeBreakup(i)}><DeleteIcon /></IconButton></Grid>
            </Grid>
          ))}

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Bank Account Details (for salary)</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}><TextField required label="Account Holder Name" fullWidth size="small" value={form.accountHolderName} onChange={setField('accountHolderName')} placeholder="As per bank records" /></Grid>
            <Grid item xs={12} md={6}><TextField required label="Account Number" fullWidth size="small" value={form.bankAccountNumber} onChange={setField('bankAccountNumber')} /></Grid>
            <Grid item xs={12} md={4}><TextField required label="IFSC Code" fullWidth size="small" value={form.bankIfscCode} onChange={setField('bankIfscCode')} inputProps={{ style: { textTransform: 'uppercase' } }} /></Grid>
            <Grid item xs={12} md={4}><TextField required label="Bank Name" fullWidth size="small" value={form.bankName} onChange={setField('bankName')} /></Grid>
            <Grid item xs={12} md={4}><TextField required label="Branch" fullWidth size="small" value={form.bankBranch} onChange={setField('bankBranch')} /></Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <TextField required label="Additional Offer Terms (shown on offer letter)" fullWidth size="small" multiline rows={2} value={form.offerTerms} onChange={setField('offerTerms')} sx={{ mb: 2 }} />
          <TextField required label="Internal Remarks" fullWidth size="small" multiline rows={2} value={form.remarks} onChange={setField('remarks')} />

          {selected && Array.isArray(form.checklist) && form.checklist.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Onboarding Checklist</Typography>
              <Grid container>
                {form.checklist.map((c, i) => (
                  <Grid item xs={12} md={6} key={c.key || i}>
                    <FormControlLabel
                      control={<Checkbox checked={!!c.done} onChange={() => toggleChecklist(i)} size="small" />}
                      label={<Typography variant="body2">{c.label}</Typography>}
                    />
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} startIcon={saving && <CircularProgress size={16} />}>
            {selected ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View dialog */}
      <Dialog open={openView} onClose={() => setOpenView(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selected?.fullName}</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Grid container spacing={1.5}>
              {[
                ['Designation', selected.designation],
                ['Department', selected.department],
                ['Employment Type', selected.employmentType],
                ['Work Location', selected.workLocation],
                ['Reporting Manager', selected.reportingManager],
                ['Email', selected.email],
                ['Contact', selected.contactNumber],
                ['Annual CTC', fmtMoney(selected.annualCtc)],
                ['Monthly Gross', fmtMoney(selected.monthlyGross)],
                ['Offer Date', fmtDate(selected.offerDate)],
                ['Joining Date', fmtDate(selected.joiningDate)],
                ['Offer Valid Till', fmtDate(selected.offerValidTill)],
                ['Status', selected.status],
                ['Offer Sent At', selected.offerLetterSentAt ? new Date(selected.offerLetterSentAt).toLocaleString('en-IN') : '—'],
                ['Account Holder', selected.accountHolderName],
                ['Account Number', selected.bankAccountNumber],
                ['IFSC', selected.bankIfscCode],
                ['Bank / Branch', selected.bankName ? `${selected.bankName}${selected.bankBranch ? ' — ' + selected.bankBranch : ''}` : ''],
              ].map(([k, v]) => (
                <Grid item xs={6} key={k}>
                  <Typography variant="caption" color="text.secondary">{k}</Typography>
                  <Typography variant="body2" fontWeight={600}>{v || '—'}</Typography>
                </Grid>
              ))}
              {Array.isArray(selected.checklist) && selected.checklist.length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Checklist ({checklistProgress(selected)}%)</Typography>
                  {selected.checklist.map((c, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckIcon fontSize="small" color={c.done ? 'success' : 'disabled'} />
                      <Typography variant="body2">{c.label}</Typography>
                    </Box>
                  ))}
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button startIcon={<PdfIcon />} onClick={() => handleDownloadOffer(selected)}>Offer Letter</Button>
          <Button onClick={() => setOpenView(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Convert to employee dialog */}
      <Dialog open={openConvert} onClose={() => setOpenConvert(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Convert to Employee</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Creates a user account for <strong>{selected?.fullName}</strong>. Leave password blank to use the default
            (<code>Welcome@123</code>); the employee must change it on first login.
          </Alert>
          {convertError && <Alert severity="error" sx={{ mb: 2 }}>{convertError}</Alert>}
          <TextField required label="Username" fullWidth size="small" sx={{ mb: 2 }} value={convertData.username} onChange={(e) => setConvertData((d) => ({ ...d, username: e.target.value }))} />
          <TextField label="Temporary Password" fullWidth size="small" sx={{ mb: 2 }} value={convertData.password} onChange={(e) => setConvertData((d) => ({ ...d, password: e.target.value }))} placeholder="Welcome@123" />
          <TextField required label="Employee ID" fullWidth size="small" sx={{ mb: 2 }} value={convertData.employeeId} onChange={(e) => setConvertData((d) => ({ ...d, employeeId: e.target.value }))} helperText="Auto-generated (next in sequence) — edit if needed" />
          {/* Designation = the actual job position (from recruitment) */}
          <FormControl required fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Designation (position)</InputLabel>
            <Select
              label="Designation (position)"
              value={convertData.designation || ''}
              onChange={(e) => setConvertData((d) => ({ ...d, designation: e.target.value, role: roleForDesignation(e.target.value) }))}
            >
              {[...new Set([convertData.designation, ...DESIGNATIONS].filter(Boolean))].map((label) => (
                <MenuItem key={label} value={label}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Access role = OneHub permissions (auto-suggested from the position) */}
          <FormControl required fullWidth size="small">
            <InputLabel>Access role</InputLabel>
            <Select label="Access role" value={convertData.role} onChange={(e) => setConvertData((d) => ({ ...d, role: e.target.value }))}>
              {ACCESS_ROLES.map((r) => <MenuItem key={r} value={r} sx={{ textTransform: 'capitalize' }}>{r}</MenuItem>)}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>Controls app permissions — suggested from the position; change if needed.</Typography>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConvert(false)}>Cancel</Button>
          <Button variant="contained" color="success" startIcon={<ConvertIcon />} onClick={handleConvert}>Create Employee</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast((t) => ({ ...t, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>{toast.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default OnboardingManagement;
